/**
 * The manifest as a form — the one screen code reads: who the playbook is
 * for and what routes to it, its mode, the report types it offers (each
 * with the template the builder opens and the configs that offer it), the
 * budget thresholds and who releases. Edited in the stored shape, so what's
 * published round-trips through the API's `Manifest.load`, which refuses
 * unknown keys.
 */

import {
  ActionIcon,
  Button,
  Group,
  NumberInput,
  Select,
  Stack,
  Table,
  Text,
  TextInput,
} from '@mantine/core';
import { IconPlus, IconX } from '@tabler/icons-react';

import { PLAYBOOK_MODES, type ManifestReportType, type PlaybookManifest } from '@/types/playbook';
import { listFrom } from './format';

interface ManifestFormProps {
  manifest: PlaybookManifest;
  onChange: (manifest: PlaybookManifest) => void;
  /** The house's manifest is read here, never edited. */
  readOnly?: boolean;
}

type ReportTypeRow = [string, ManifestReportType];

function rows(manifest: PlaybookManifest): ReportTypeRow[] {
  return Object.entries(manifest.report_types ?? {});
}

export function ManifestForm({
  manifest,
  onChange,
  readOnly = false,
}: Readonly<ManifestFormProps>) {
  const client = manifest.client ?? { code: '', teams: [], configs: [] };
  const set = (patch: Partial<PlaybookManifest>) => onChange({ ...manifest, ...patch });
  const setClient = (patch: Partial<typeof client>) =>
    onChange({ ...manifest, client: { ...client, ...patch } });
  const setRows = (next: ReportTypeRow[]) =>
    onChange({ ...manifest, report_types: Object.fromEntries(next) });
  const types = rows(manifest);
  const isClient = manifest.kind === 'client';

  return (
    <Stack gap="sm" data-testid="manifest-form">
      <Group grow align="flex-start">
        <TextInput
          label="Name"
          value={manifest.name}
          onChange={(e) => set({ name: e.currentTarget.value })}
          readOnly={readOnly}
          data-testid="manifest-name"
        />
        <Select
          label="Mode"
          description="Live: the workbench owns the client's new cases"
          data={PLAYBOOK_MODES.map((mode) => ({ value: mode, label: mode }))}
          value={manifest.mode ?? 'off'}
          onChange={(value) => value && set({ mode: value as PlaybookManifest['mode'] })}
          readOnly={readOnly || !isClient}
          disabled={!isClient}
          allowDeselect={false}
          data-testid="manifest-mode"
        />
        <Select
          label="Release"
          description="Who releases a draft as the report"
          data={[
            { value: '', label: "The house's" },
            { value: 'analyst', label: 'An analyst' },
            { value: 'automatic', label: 'The workbench' },
          ]}
          value={manifest.release ?? ''}
          onChange={(value) =>
            set({ release: value ? (value as PlaybookManifest['release']) : null })
          }
          readOnly={readOnly}
          data-testid="manifest-release"
        />
      </Group>
      {isClient ? (
        <Group grow align="flex-start">
          <TextInput
            label="Client code"
            description="The client's Backoffice code"
            value={client.code}
            onChange={(e) => setClient({ code: e.currentTarget.value })}
            readOnly={readOnly}
            data-testid="manifest-client-code"
          />
          <TextInput
            label="Teams"
            description="Hub team ids, comma-separated"
            value={client.teams.join(', ')}
            onChange={(e) => setClient({ teams: listFrom(e.currentTarget.value) })}
            readOnly={readOnly}
            data-testid="manifest-teams"
          />
          <TextInput
            label="Configs"
            description="Report config slugs covered, comma-separated"
            value={client.configs.join(', ')}
            onChange={(e) => setClient({ configs: listFrom(e.currentTarget.value) })}
            readOnly={readOnly}
            data-testid="manifest-configs"
          />
        </Group>
      ) : null}
      <Group grow align="flex-start">
        <NumberInput
          label="Case budget, $"
          description="The threshold on model spend across the case — raised by a person, never a wall"
          min={1}
          value={manifest.budget_usd ?? ''}
          onChange={(value) => set({ budget_usd: value === '' ? null : Number(value) })}
          readOnly={readOnly}
          data-testid="manifest-budget"
        />
        <NumberInput
          label="Budget before Start, $"
          description="What runs may spend before a person has started the case"
          min={0}
          value={manifest.budget_before_start_usd ?? ''}
          onChange={(value) =>
            set({ budget_before_start_usd: value === '' ? null : Number(value) })
          }
          readOnly={readOnly}
          data-testid="manifest-budget-before-start"
        />
      </Group>
      {isClient ? (
        <Stack gap={4}>
          <Group justify="space-between">
            <Text size="sm" fw={600}>
              Report types
            </Text>
            {!readOnly ? (
              <Button
                size="compact-xs"
                variant="default"
                leftSection={<IconPlus size={12} />}
                onClick={() => setRows([...types, ['', { name: '', template: null, configs: [] }]])}
                data-testid="manifest-add-type"
              >
                Add a type
              </Button>
            ) : null}
          </Group>
          <Text size="xs" c="dimmed">
            The client&apos;s own codes. A template is a .docx under templates/ in the folder;
            configs are the slugs that offer the type (none: every config the client covers).
          </Text>
          {types.length === 0 ? (
            <Text size="xs" c="dimmed">
              No report types yet.
            </Text>
          ) : (
            <Table verticalSpacing={4} fz="sm">
              <Table.Thead>
                <Table.Tr>
                  <Table.Th w={100}>Code</Table.Th>
                  <Table.Th>Name</Table.Th>
                  <Table.Th>Template</Table.Th>
                  <Table.Th>Configs</Table.Th>
                  {!readOnly ? <Table.Th w={40} /> : null}
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {types.map(([code, type], at) => {
                  const update = (next: ReportTypeRow) =>
                    setRows(types.map((row, i) => (i === at ? next : row)));
                  return (
                    <Table.Tr key={at} data-testid={`manifest-type-${at}`}>
                      <Table.Td>
                        <TextInput
                          size="xs"
                          value={code}
                          onChange={(e) => update([e.currentTarget.value.trim(), type])}
                          readOnly={readOnly}
                          aria-label="Code"
                        />
                      </Table.Td>
                      <Table.Td>
                        <TextInput
                          size="xs"
                          value={type.name}
                          onChange={(e) => update([code, { ...type, name: e.currentTarget.value }])}
                          readOnly={readOnly}
                          aria-label="Name"
                        />
                      </Table.Td>
                      <Table.Td>
                        <TextInput
                          size="xs"
                          placeholder="templates/type-2b.docx"
                          value={type.template ?? ''}
                          onChange={(e) =>
                            update([
                              code,
                              { ...type, template: e.currentTarget.value.trim() || null },
                            ])
                          }
                          readOnly={readOnly}
                          aria-label="Template"
                        />
                      </Table.Td>
                      <Table.Td>
                        <TextInput
                          size="xs"
                          placeholder="every config"
                          value={(type.configs ?? []).join(', ')}
                          onChange={(e) =>
                            update([code, { ...type, configs: listFrom(e.currentTarget.value) }])
                          }
                          readOnly={readOnly}
                          aria-label="Configs"
                        />
                      </Table.Td>
                      {!readOnly ? (
                        <Table.Td>
                          <ActionIcon
                            size="sm"
                            variant="subtle"
                            color="gray"
                            aria-label={`Remove ${code || 'the type'}`}
                            onClick={() => setRows(types.filter((_, i) => i !== at))}
                          >
                            <IconX size={14} />
                          </ActionIcon>
                        </Table.Td>
                      ) : null}
                    </Table.Tr>
                  );
                })}
              </Table.Tbody>
            </Table>
          )}
        </Stack>
      ) : null}
    </Stack>
  );
}
